// src/pages/dashboard/PayrollDeductions.js
// UPDATED: Date range now optional + auto-populate amount

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  DatePicker,
  Table,
  Space,
  InputNumber,
  message,
  Typography,
  Card,
  Checkbox,
  Select,
  Tooltip,
  Alert
} from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  PercentageOutlined,
  DollarOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { supabase } from '../../services/supabase';
import dayjs from 'dayjs';
import { StatusPill, formatRs } from '../../components/ui';
import isBetween from 'dayjs/plugin/isBetween';

dayjs.extend(isBetween);

const { Text } = Typography;
const { Option } = Select;

const PayrollDeductions = ({ visible, employee, onClose, companyId }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deductions, setDeductions] = useState([]);
  const [deductionTypes, setDeductionTypes] = useState([]);
  const [selectedDeductions, setSelectedDeductions] = useState([]);
  const [selectedDeductionType, setSelectedDeductionType] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible && employee && companyId) {
      fetchDeductionTypes();
      fetchEmployeeDeductions();
      setSelectedDeductions([]);
      setSelectedDeductionType(null);
    }
  }, [visible, employee, companyId]);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setSelectedDeductions([]);
      setDeductions([]);
      setDeductionTypes([]);
      setSelectedDeductionType(null);
      setLoading(false);
      setSubmitting(false);
    }
  }, [visible, form]);

  const fetchDeductionTypes = async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('deduction_types')
        .select('*')
        .eq('company_id', companyId)
        .order('name');

      if (error) throw error;
      setDeductionTypes(data || []);
    } catch (error) {
      console.error('Error fetching deduction types:', error);
      message.error('Failed to fetch deduction types');
      setDeductionTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeDeductions = async () => {
    if (!employee?.employeeId || !companyId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('employee_deductions')
        .select(`
          id,
          amount,
          start_date,
          end_date,
          deduction_type_id,
          deduction_types (
            id,
            name,
            is_percentage
          )
        `)
        .eq('employee_id', employee.employeeId)
        .eq('company_id', companyId)
        .order('start_date', { ascending: false });

      if (error) throw error;
      setDeductions(data || []);
    } catch (error) {
      console.error('Error fetching employee deductions:', error);
      message.error('Failed to fetch employee deductions');
      setDeductions([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle deduction type selection - auto-populate amount
  const handleDeductionTypeChange = (deductionTypeId) => {
    const selectedType = deductionTypes.find(dt => dt.id === deductionTypeId);
    setSelectedDeductionType(selectedType);

    if (selectedType) {
      form.setFieldsValue({
        amount: selectedType.amount
      });
    }
  };

  const handleAddDeduction = async () => {
    if (!companyId || !employee?.employeeId) {
      message.error('Missing required information');
      return;
    }

    try {
      const values = await form.validateFields();
      setSubmitting(true);

      // Handle optional date range
      let startDate = null;
      let endDate = null;

      if (values.date_range && values.date_range.length === 2) {
        startDate = values.date_range[0].format('YYYY-MM-DD');
        endDate = values.date_range[1].format('YYYY-MM-DD');
      }

      const deductionData = {
        company_id: companyId,
        employee_id: employee.employeeId,
        deduction_type_id: values.deduction_type_id,
        amount: values.amount,
        start_date: startDate,
        end_date: endDate,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('employee_deductions')
        .insert([deductionData])
        .select()
        .single();

      if (error) throw error;

      message.success('Deduction added successfully');
      form.resetFields();
      setSelectedDeductionType(null);
      await fetchEmployeeDeductions();

    } catch (validationError) {
      if (validationError.errorFields) {
        message.error('Please fill in all required fields');
      } else {
        console.error('Error adding deduction:', validationError);
        message.error('Failed to add deduction');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveDeduction = async (deductionId) => {
    if (!deductionId) return;

    try {
      setLoading(true);
      const { error } = await supabase
        .from('employee_deductions')
        .delete()
        .eq('id', deductionId);

      if (error) throw error;

      message.success('Deduction removed successfully');
      setSelectedDeductions(prev => prev.filter(id => id !== deductionId));
      await fetchEmployeeDeductions();

    } catch (error) {
      console.error('Error removing deduction:', error);
      message.error('Failed to remove deduction');
    } finally {
      setLoading(false);
    }
  };

  // Calculate deduction amount - handle optional date range
  const calculateDeductionAmount = (deduction) => {
    // If no date range, it's always active
    if (!deduction.start_date || !deduction.end_date) {
      const baseGrossPay = (employee?.gross_pay || 0) - (employee?.total_allowances || 0);

      if (deduction.deduction_types?.is_percentage) {
        return baseGrossPay * (deduction.amount / 100);
      }

      return deduction.amount;
    }

    // Check if within date range
    const today = dayjs();
    const startDate = dayjs(deduction.start_date);
    const endDate = dayjs(deduction.end_date);
    const isActive = today.isBetween(startDate, endDate, 'day', true);

    if (!isActive) return 0;

    const baseGrossPay = (employee?.gross_pay || 0) - (employee?.total_allowances || 0);

    if (deduction.deduction_types?.is_percentage) {
      return baseGrossPay * (deduction.amount / 100);
    }

    return deduction.amount;
  };

  const handleSave = async () => {
    if (!employee?.payrollRecordId || !companyId) {
      message.error('Missing payroll record information');
      return;
    }

    try {
      setLoading(true);

      const payrollPeriodDeductions = deductions
        .filter(d => selectedDeductions.includes(d.id))
        .reduce((sum, d) => sum + calculateDeductionAmount(d), 0);

      const { data: currentRecord, error: fetchError } = await supabase
        .from('payroll_records')
        .select('gross_pay, total_allowances')
        .eq('id', employee.payrollRecordId)
        .eq('company_id', companyId)
        .single();

      if (fetchError) throw fetchError;

      const newNetPay = (currentRecord.gross_pay || 0) - payrollPeriodDeductions;

      const { error: updateError } = await supabase
        .from('payroll_records')
        .update({
          total_deductions: payrollPeriodDeductions,
          net_pay: newNetPay,
          updated_at: new Date().toISOString()
        })
        .eq('id', employee.payrollRecordId)
        .eq('company_id', companyId);

      if (updateError) throw updateError;

      message.success('Deductions saved successfully');
      handleCancel();

    } catch (error) {
      console.error('Error saving deductions:', error);
      message.error('Error saving deductions');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedDeductions([]);
    setSelectedDeductionType(null);
    setLoading(false);
    setSubmitting(false);
    onClose();
  };

  const validateDateRange = (rule, value) => {
    if (!value) return Promise.resolve(); // Optional

    const [start, end] = value;
    if (start && end) {
      if (end.isBefore(start)) {
        return Promise.reject('End date cannot be before start date');
      }
    }
    return Promise.resolve();
  };

  const columns = [
    {
      title: 'Apply',
      key: 'apply',
      width: 80,
      render: (_, record) => {
        // If no date range, always active
        let isActive = true;

        if (record.start_date && record.end_date) {
          const today = dayjs();
          const startDate = dayjs(record.start_date);
          const endDate = dayjs(record.end_date);
          isActive = today.isBetween(startDate, endDate, 'day', true);
        }

        return (
          <Checkbox
            checked={selectedDeductions.includes(record.id)}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedDeductions(prev => [...prev, record.id]);
              } else {
                setSelectedDeductions(prev => prev.filter(id => id !== record.id));
              }
            }}
            disabled={!isActive}
          />
        );
      },
    },
    {
      title: 'Deduction Type',
      key: 'type',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Space>
            <Text strong>{record.deduction_types?.name || '-'}</Text>
            {record.deduction_types?.is_percentage ? (
              <StatusPill tone="primary">Percentage</StatusPill>
            ) : (
              <StatusPill tone="neutral">Fixed</StatusPill>
            )}
          </Space>
        </Space>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount, record) => {
        const calculatedAmount = calculateDeductionAmount(record);

        if (record.deduction_types?.is_percentage) {
          const baseGrossPay = (employee?.gross_pay || 0) - (employee?.total_allowances || 0);
          return (
            <Space direction="vertical" size="small">
              <span className="num num--negative">{formatRs(calculatedAmount, 2)}</span>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {amount}% of Rs{baseGrossPay.toFixed(2)}
              </Text>
            </Space>
          );
        }
        return <span className="num num--negative">{formatRs(amount, 2)}</span>;
      },
    },
    {
      title: 'Active Period',
      key: 'period',
      render: (_, record) => {
        if (!record.start_date || !record.end_date) {
          return <span className="text-muted">Always active</span>;
        }
        return (
          <span>
            {dayjs(record.start_date).format('DD/MM/YYYY')} - {dayjs(record.end_date).format('DD/MM/YYYY')}
          </span>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        if (!record.start_date || !record.end_date) {
          return <StatusPill tone="positive">Active</StatusPill>;
        }

        const today = dayjs();
        const startDate = dayjs(record.start_date);
        const endDate = dayjs(record.end_date);
        const isActive = today.isBetween(startDate, endDate, 'day', true);
        const isFuture = startDate.isAfter(today);
        const isPast = endDate.isBefore(today);

        if (isActive) {
          return <StatusPill tone="positive">Active</StatusPill>;
        } else if (isFuture) {
          return <StatusPill tone="primary">Scheduled</StatusPill>;
        } else if (isPast) {
          return <StatusPill tone="neutral">Expired</StatusPill>;
        }
        return <StatusPill tone="neutral">Unknown</StatusPill>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveDeduction(record.id)}
          loading={loading}
          disabled={submitting}
        />
      ),
    },
  ];

  const totalDeductions = deductions.reduce((sum, d) => {
    if (!selectedDeductions.includes(d.id)) return sum;
    return sum + calculateDeductionAmount(d);
  }, 0);

  const baseGrossPay = (employee?.gross_pay || 0) - (employee?.total_allowances || 0);
  const netPay = (employee?.gross_pay || 0) - totalDeductions;

  const activeDeductions = deductions.filter(d => {
    // If no date range, always active
    if (!d.start_date || !d.end_date) return true;

    const today = dayjs();
    const startDate = dayjs(d.start_date);
    const endDate = dayjs(d.end_date);
    return today.isBetween(startDate, endDate, 'day', true);
  });

  const tableHeader = (
    <div>
      <Space>
        <Checkbox
          checked={selectedDeductions.length === activeDeductions.length && activeDeductions.length > 0}
          indeterminate={selectedDeductions.length > 0 && selectedDeductions.length < activeDeductions.length}
          onChange={(e) => {
            if (e.target.checked) {
              const activeDeductionIds = activeDeductions.map(d => d.id);
              setSelectedDeductions(activeDeductionIds);
            } else {
              setSelectedDeductions([]);
            }
          }}
          disabled={activeDeductions.length === 0}
        >
          Select All Active Deductions ({activeDeductions.length})
        </Checkbox>
      </Space>
    </div>
  );

  const getPreviewAmount = () => {
    if (!selectedDeductionType) return null;

    if (selectedDeductionType.is_percentage) {
      const amount = form.getFieldValue('amount') || selectedDeductionType.amount;
      return baseGrossPay * (amount / 100);
    }

    return form.getFieldValue('amount') || selectedDeductionType.amount;
  };

  const previewAmount = getPreviewAmount();

  return (
    <Modal
      title={
        <Space>
          <MinusCircleOutlined />
          {`Deductions - ${employee?.employees?.first_name} ${employee?.employees?.last_name}`}
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      width={950}
      maskClosable={false}
      keyboard={false}
      footer={[
        <Button
          key="cancel"
          onClick={handleCancel}
          disabled={submitting}
        >
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={loading}
          onClick={handleSave}
          disabled={submitting}
        >
          Save Deductions
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <div className="soft-panel kv-list">
          <div>
            <span className="kv-list__label">Department</span>
            <span className="kv-list__value">{employee?.employees?.department || 'N/A'}</span>
          </div>
          <div>
            <span className="kv-list__label">Position</span>
            <span className="kv-list__value">{employee?.employees?.position || 'N/A'}</span>
          </div>
          <div>
            <span className="kv-list__label">
              Base gross pay (before allowances){' '}
              <Tooltip title="Percentage deductions are calculated on this amount">
                <InfoCircleOutlined />
              </Tooltip>
            </span>
            <span className="kv-list__value">{formatRs(baseGrossPay, 2)}</span>
          </div>
          <div>
            <span className="kv-list__label">Allowances</span>
            <span className="kv-list__value">{formatRs(employee?.total_allowances || 0, 2)}</span>
          </div>
          <div>
            <span className="kv-list__label">Gross pay (with allowances)</span>
            <span className="kv-list__value">{formatRs(employee?.gross_pay || 0, 2)}</span>
          </div>
        </div>

        <Card title="Add New Deduction" size="small">
          <Form
            form={form}
            layout="vertical"
            style={{ marginBottom: 0 }}
          >
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'end' }}>
              <Form.Item
                name="deduction_type_id"
                label="Deduction Type"
                rules={[{ required: true, message: 'Please select deduction type' }]}
                style={{ minWidth: '250px', flex: 1 }}
              >
                <Select
                  placeholder="Select Deduction Type"
                  loading={loading}
                  disabled={submitting}
                  showSearch
                  optionFilterProp="children"
                  onChange={handleDeductionTypeChange}
                >
                  {deductionTypes.map(type => (
                    <Option key={type.id} value={type.id}>
                      {type.is_percentage ? <PercentageOutlined /> : <DollarOutlined />}
                      {' '}{type.name} {type.is_percentage ? `(${type.amount}%)` : `(Rs${type.amount})`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="amount"
                label={
                  selectedDeductionType?.is_percentage
                    ? "Percentage (%)"
                    : "Amount (Rs)"
                }
                rules={[
                  { required: true, message: 'Please enter amount' },
                  { type: 'number', min: 0.01, message: 'Amount must be greater than 0' }
                ]}
                style={{ minWidth: '120px' }}
                tooltip={
                  selectedDeductionType?.is_percentage
                    ? `Auto-filled from ${selectedDeductionType.name}. You can adjust if needed.`
                    : 'You can adjust this amount if needed'
                }
              >
                <InputNumber
                  placeholder={selectedDeductionType?.is_percentage ? "%" : "Amount"}
                  min={0}
                  step={selectedDeductionType?.is_percentage ? 0.1 : 0.01}
                  max={selectedDeductionType?.is_percentage ? 100 : undefined}
                  style={{ width: '100%' }}
                  disabled={submitting || !selectedDeductionType}
                  addonAfter={selectedDeductionType?.is_percentage ? '%' : 'Rs'}
                />
              </Form.Item>

              <Form.Item
                name="date_range"
                label={
                  <Space>
                    Active Period
                    <Tooltip title="Optional: Leave empty for always-active deductions (e.g., CSG, Levy)">
                      <InfoCircleOutlined style={{ color: 'var(--theme-muted)' }} />
                    </Tooltip>
                  </Space>
                }
                rules={[{ validator: validateDateRange }]}
                style={{ minWidth: '250px' }}
              >
                <DatePicker.RangePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  disabled={submitting}
                  placeholder={['Start (Optional)', 'End (Optional)']}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddDeduction}
                  loading={submitting}
                  disabled={loading || !selectedDeductionType}
                >
                  Add Deduction
                </Button>
              </Form.Item>
            </div>

            {selectedDeductionType && previewAmount !== null && (
              <Alert
                message="Deduction Preview"
                description={
                  selectedDeductionType.is_percentage ? (
                    <Text>
                      {form.getFieldValue('amount') || selectedDeductionType.amount}% of {formatRs(baseGrossPay, 2)} = <span className="num num--negative">{formatRs(previewAmount, 2)}</span>
                    </Text>
                  ) : (
                    <Text>
                      Fixed amount: <span className="num num--negative">{formatRs(previewAmount, 2)}</span>
                    </Text>
                  )
                }
                type="info"
                showIcon
                style={{ marginTop: 12 }}
              />
            )}
          </Form>
        </Card>

        <Table
          columns={columns}
          dataSource={deductions}
          rowKey="id"
          pagination={false}
          loading={loading && !submitting}
          title={() => tableHeader}
          summary={() => deductions.length > 0 ? (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}></Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <Text strong>Selected Deductions Total</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  <span className="num num--negative">{formatRs(totalDeductions, 2)}</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3}></Table.Summary.Cell>
                <Table.Summary.Cell index={4}></Table.Summary.Cell>
                <Table.Summary.Cell index={5}></Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          ) : null}
        />

        <Card size="small">
          <div className="totals">
            <div className="totals__row">
              <span>Gross pay (with allowances)</span>
              <span className="num">{formatRs(employee?.gross_pay || 0, 2)}</span>
            </div>
            <div className="totals__row">
              <span>Selected deductions</span>
              <span className="num num--negative">-{formatRs(totalDeductions, 2)}</span>
            </div>
            <div className="totals__row totals__row--grand">
              <span>Net pay</span>
              <span className="num">{formatRs(netPay, 2)}</span>
            </div>
          </div>
        </Card>
      </Space>
    </Modal>
  );
};

export default PayrollDeductions;